import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Modal, Button, Spin, message, QRCode } from 'antd';
import { CheckCircleFilled, AlipayCircleFilled, WechatFilled } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { BASE_URL, PROFILE_INFO_URL, SUBSCRIPTIONS_PLAN_URL, SUBSCRIPTIONS_VIP_URL, SUBSCRIPTIONS_PAYMENT_URL } from '../constants/Url';
import httpClient from '../utils/HttpClient';
import { debounce } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { loginSuccessAction } from '../modules/auth/actions';
import { storageKeys } from '../constants/storage';
import { BsCheckCircleFill, BsCircle, BsInfo } from "react-icons/bs";

const SuperVIPModal = ({ open, onCancel, onSuccess, fetchCourse }) => {
  const { t } = useTranslation();
  const [plans, setPlans] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('alipay');
  const [transactionData, setTransactionData] = useState(null);
  const [profile, setProfile] = useState([]);
  const [couponCode, setCouponCode] = useState('');
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [couponDiscount, setCouponDiscount] = useState(0);
  const user = useSelector((state) => state.authReducer);
  let token = user && user?.token ? user?.token : null;
  const dispatch = useDispatch();

  const runTimer = useRef(null);
  const timer = useRef(null);
  const [paymentQrcode, setPaymentQrcode] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [checkPaymentStatusParam, setCheckPaymentStatusParam] = useState(null);
  const [showCloseModal, setShowCloseModal] = useState(false);

  const handleClose = () => {
    setIsPaymentModalOpen(false);
    setPaymentQrcode(null);
    setErrorMessage(null);
    setCheckPaymentStatusParam(null);
    runTimer.current = false;
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    setCouponCode("");
    setCouponDiscount(0);
    setPaymentMethod("alipay");
  }

  useEffect(() => {
    return () => {
      setPaymentQrcode(null);
      setErrorMessage(null);
      setCheckPaymentStatusParam(null);
      runTimer.current = false;
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = null;
      }
    }
  }, []);

  const checkPaymentStatus = async (subscriptionId, method, payUrl) => {
    if (!runTimer.current) return;
    setErrorMessage(null);
    try {
      if (timer.current) {
        clearTimeout(timer.current);
      }
      timer.current = null;
      const res = await httpClient.post(`/subscriptions/payment-query`, {
        method: method,
        subscriptionId: subscriptionId
      });
      if (res.data && res.data.status === 200) {
        const order = res.data.data || res.data;
        if (order.paymentStatus === "paid") {
          runTimer.current = false;
            const successMsg = t('text_operation_success');
            message.success(successMsg);
            if (fetchCourse) fetchCourse();
            setIsPaymentModalOpen(false);
            await fetchProfiles();
            if (onSuccess) onSuccess();
            if (onCancel) onCancel();
          return;
        }
      } else {
        runTimer.current = false;
        setErrorMessage(t('text_failed_loading_order_detail'));
      }
      timer.current = setTimeout(() => {
        checkPaymentStatus(subscriptionId, method, payUrl);
      }, 3000);
    } catch (err) {
      runTimer.current = false;
      console.error('Failed to check order status:', err);
      setErrorMessage(err.message || 'Error checking order status');
    }
  };

  useEffect(() => {
    if (open) {
      fetchPlans();
    }
  }, [open]);

  const saveAuth = (token, user, refreshToken) => {
    dispatch(loginSuccessAction({ token, user: user, photo: user?.picUrl }));
    localStorage.setItem(storageKeys.TOKEN, btoa(token));
    localStorage.setItem(storageKeys.USER, JSON.stringify(user));
  };

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const res = await httpClient.get(SUBSCRIPTIONS_PLAN_URL)
        .then(r => r.data)
        .catch(e => ({ status: 500, message: e?.message }));
      
      if (res && res.status === 200 && res.data) {
        setPlans(res.data);
      }
    } catch (error) {
      console.error('fetch plans error', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProfiles = async () => {
    try {
      const res = await httpClient.get(PROFILE_INFO_URL);
      const member = res?.data?.member;
      const status = res?.data?.status;

      if (status === 200) {
        setProfile(member);
        saveAuth(token, member)
      } else {
        setProfile([]);
      }
    } catch (error) {
      console.error("fetchData -> error", error);
      setProfile([]);
    }
  };
  
  const debouncedFetchAll = useCallback(
    debounce(async () => {
      await Promise.all([fetchProfiles()])
    }, 300),
    []
  );

  useEffect(() => {
    debouncedFetchAll();
    return debouncedFetchAll.cancel;
  }, [debouncedFetchAll])

  const handleOpenPaymentModal = () => {
    setIsPaymentModalOpen(true);
  };

  const handleClearCoupon = () => {
    setCouponDiscount(0);
    setCouponCode('');
    message.success(t('text_coupon_cancelled'));
  };

  //Apply Coupon
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      message.error(t('text_input_coupon'));
      return;
    }
    setApplyingCoupon(true);
    try {
      const res = await httpClient.post('/coupons-scan-validate', {
        couponCode: couponCode.trim(),
        currentOrderTotal: plans?.super?.amount
      });
      if (res?.data?.status === 200) {
        const data = res?.data?.data || res?.data;
        const discount = data?.discountAmount || data?.discount || 0;
        message.success(t("text_coupon_success"));
        setCouponDiscount(discount);
      } else {
        message.error(t(res?.data?.message));
        setCouponCode("")
      }
    } catch (err) {
      message.error(t(err?.response?.data?.message || err?.message) || t('text_coupon_failed'));
      setCouponCode("")
    } finally {
      setApplyingCoupon(false);
    }
  };


  const handleConfirmPayment = async () => {
    try {
      setApplyLoading(true);
      
      const planId = plans?.super?.planId;

      // 1. Call order creation API
      const proResponse = await httpClient.put(SUBSCRIPTIONS_VIP_URL, {
        planId: planId,
        method: paymentMethod,
        couponCode: couponCode
      });
      
      const proRes = proResponse?.data;
      
      if (proRes && (proRes.status === 200 || proRes.status === 201 || String(proRes.status) === '200' || proRes.code === 200 || proRes.code === 0)) {
        
        const transactionData = proRes.data || proRes;
        const payUrl = transactionData?.paymentUrl || transactionData?.payUrl || transactionData?.redirectUrl || transactionData?.url || proRes?.paymentUrl || proRes?.payUrl;

        // If backend returns an external payment gateway URL (Alipay/WeChat), redirect user to pay
        if (payUrl) {
          window.location.href = payUrl;
          return;
        }

        var code_url = null;
        if (paymentMethod === 'alipay') {
          code_url = transactionData?.orderinfo?.qrCode;
        } else if (paymentMethod === 'wechatpay') {
          code_url = transactionData?.orderinfo?.code_url;
        }
        setPaymentQrcode(code_url);

        if (code_url) {
          runTimer.current = true;
          setCheckPaymentStatusParam({ subscriptionId: transactionData.subscriptionId, method: paymentMethod, payUrl: payUrl });
          checkPaymentStatus(transactionData.subscriptionId, paymentMethod, payUrl);
        }
      } else {
        const errorMsg = typeof proRes?.message === 'string' ? t(proRes.message) : (t(proRes?.msg) || t('text_operation_failed'));
        message.error(errorMsg);
      }
    } catch (error) {
      console.error('payment error', error);
      
      const errorData = error?.response?.data || error;
      const errorMsg = typeof errorData?.message === 'string' ? t(errorData.message) : (error?.message || t('text_operation_failed'));
      message.error(errorMsg);
    } finally {
      setApplyLoading(false);
    }
  };

  const superFeatures = [
    t('text_svip_benefit_1'),
    t('text_svip_benefit_2'),
    t('text_svip_benefit_3'),
    t('text_svip_benefit_4'),
  ];

  return (
    <Modal
      title={<div className="text-center text-xl font-bold py-3">{t('text_activate_svip')}</div>}
      open={open}
      onCancel={onCancel}
      footer={null}
      width={420}
      centered
      className="super-vip-modal"
      styles={{ body: { padding: '0 0 24px 0' } }}
    >
      <Spin spinning={loading}>
        <div className="flex flex-col items-center">
          {/* Illustration Image */}
          <div className="w-full flex justify-center mb-4 px-4 pt-2">
            <img 
              src={BASE_URL + "/super-vip-illustration.png"} 
              alt="Super VIP Illustration" 
              className="w-full max-w-[300px] h-auto object-contain"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>

          {/* Super VIP Card */}
          <div className="px-6 w-full">
            <div className="border border-[#d81557] rounded-2xl p-6 bg-[#cf3d6b] relative shadow-md flex flex-col">
              <div className="flex items-center justify-center gap-3 mb-6">
                <span className="text-white font-serif text-2xl font-bold tracking-widest">
                  <img alt='ESI-Logo' role='button' src={BASE_URL + "/logo.svg"} className='esi-logo brightness-0 invert h-6' />
                </span>
                <span className="bg-[#ffe58f] text-[#cf3d6b] text-xs px-2 py-0.5 rounded-full font-bold">
                  {t('text_svip_member')}
                </span>
              </div>

              <div className="flex-1 space-y-3 mb-8">
                {superFeatures.map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <CheckCircleFilled className="text-white mt-1" />
                    <span className="text-white text-sm font-medium">{feature}</span>
                  </div>
                ))}
              </div>

              <div className="mt-auto text-center">
                <Button 
                  onClick={handleOpenPaymentModal}
                  className="w-full bg-white hover:bg-gray-100 text-[#cf3d6b] border-none h-[44px] rounded-lg text-base font-bold mb-3 cursor-pointer"
                >
                  {t('text_activate_now')}
                </Button>
                <div className="text-white text-sm opacity-90">
                  &yen; {plans?.super?.amount ?? 199.00} / {plans?.super?.unit === 'month' ? t('text_month') : (plans?.super?.unit === 'day' ? t('text_day_no') : t('text_year'))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Spin>

      <Modal
        open={isPaymentModalOpen}
        onCancel={() => {
          if (paymentQrcode) {
            setShowCloseModal(true);
          } else {
            handleClose();
          }
        }}
        footer={null}
        title={null}
        closable={true}
        width={450}
        centered
        styles={{ body: { padding: '24px', borderRadius: '12px' } }}
      >
        {paymentQrcode ? (
          <>
          <div className="flex flex-col items-center justify-center py-2">
            <div className="text-lg font-bold text-gray-800 text-center mb-4">{t('text_tobepaid_qrcode')}</div>
            <QRCode
              value={paymentQrcode}
              size={220}
              icon={paymentMethod === 'wechatpay' ? "https://ts4.tc.mm.bing.net/th/id/ODF.BvtHqZTl6qLypPDIASUGoA?w=32&h=32&qlt=97&pcl=fffffa&o=6&pid=1.2" : "https://ts1.tc.mm.bing.net/th/id/ODF.tFQwCWKfvMeJYOFxHknauw?w=32&h=32&qlt=90&pcl=fffffa&o=6&pid=1.2"}
              iconSize={{
                width: 32,
                height: 32,
              }}
            />
          </div>
          <div style={{marginTop: 10, marginBottom: errorMessage ? 10 : 0, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 5}}>
            <div className="bg-[#fff0f4] p-0.5 rounded-full">
              <BsInfo className="text-[#e80d44] text-xl shrink-0" />
            </div>
            <span className="text-[#e80d44] text-sm font-medium">{t("text_qrcode_desc")}</span>
          </div>
          {errorMessage ? <div className="flex flex-col items-center justify-center w-full">
            <span style={{color: 'red'}} className="text-sm text-gray-500 font-medium">{errorMessage}</span>

            <button
              onClick={() => {
                checkPaymentStatus(checkPaymentStatusParam.orderId, checkPaymentStatusParam.method, checkPaymentStatusParam.payUrl);
              }}
              style={{width: '100%'}}
              className={`rounded-xl bg-gradient-to-r mt-4 from-[#e80d44] to-[#ff3b68] text-white px-10 py-3 font-semibold text-sm transition-all duration-300 flex justify-center items-center gap-2 hover:scale-105 active:scale-95`}
            >
              {t("text_retry")}
            </button>
          </div> : null}
          </>
        ) : (
          <div className="flex flex-col gap-4 mt-2">
            <div className="text-lg font-bold text-gray-800 text-center mb-2">{t('text_select_payment')}</div>
            
            <div 
              className={`flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer transition-all ${paymentMethod === 'alipay' ? 'border-[#e91e63] bg-[#fdf2f5]' : 'border-gray-100 hover:border-gray-200'}`}
              onClick={() => setPaymentMethod('alipay')}
            >
              <div className="flex items-center gap-3">
                <AlipayCircleFilled className="text-[#1677ff] text-[28px]" />
                <span className="font-semibold text-gray-800 text-base">{t('text_alipay_payment')}</span>
              </div>
              {paymentMethod === 'alipay' ? <CheckCircleFilled className="text-[#52c41a] text-xl" /> : <div className="w-[20px] h-[20px] rounded-full border-2 border-gray-300"></div>}
            </div>

            <div 
              className={`flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer transition-all ${paymentMethod === 'wechatpay' ? 'border-[#e91e63] bg-[#fdf2f5]' : 'border-gray-100 hover:border-gray-200'}`}
              onClick={() => setPaymentMethod('wechatpay')}
            >
              <div className="flex items-center gap-3">
                <WechatFilled className="text-[#09b83e] text-[28px]" /> 
                <span className="font-semibold text-gray-800 text-base">{t('text_wechat_payment')}</span>
              </div>
              {paymentMethod === 'wechatpay' ? <CheckCircleFilled className="text-[#52c41a] text-xl" /> : <div className="w-[20px] h-[20px] rounded-full border-2 border-gray-300"></div>}
            </div>

            {/* Coupon Section */}
            {
              couponDiscount > 0 ? "" :
              <>
              <h3 className="text-sm font-semibold text-gray-900">{t("text_use_coupon")}</h3>
            <div className="flex gap-2" style={{marginTop:"-0.5rem"}}>
              <input
                type="text"
                placeholder={t("text_input_coupon")}
                className="flex-1 border-2 border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-gray-200"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value)}
              />
              <button
                onClick={handleApplyCoupon}
                disabled={applyingCoupon}
                className="bg-[#e80d44] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#d00b3c] transition-colors disabled:opacity-50"
              >
                {applyingCoupon ? t('text_applying') : t('text_exchange')}
              </button>
            </div>
            </>
            }

            <div className="space-y-3 bg-[#fcfcfc] border border-gray-100 rounded-xl p-4 mt-2 shadow-sm">
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-500">{t("text_product_total")}</span>
                <span className="text-gray-900 font-medium">¥{Number(plans?.super?.amount || 199.00).toFixed(2)}</span>
              </div>
              
              {couponDiscount > 0 && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-500">{t("text_coupon_deduct")}</span>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 bg-[#f7f8fc] border border-dashed border-[#d4d8ef] text-[#5e6db0] px-2.5 py-1 rounded-md shadow-sm" style={{marginRight:"2rem"}}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="1.1em" height="1.1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"/>
                        <path d="M9 5v14"/>
                      </svg>
                      <span className="text-xs font-bold tracking-wide">{couponCode}</span>
                      <button 
                        onClick={handleClearCoupon}
                        className="hover:text-[#e80d44] transition-colors ml-1 cursor-pointer flex items-center justify-center"
                        title={t("text_cancel_coupon")}
                      >
                        <svg stroke="currentColor" fill="none" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round" height="1.1em" width="1.1em"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                      </button>
                    </div>
                    <span className="text-[#e80d44] font-medium">-¥{couponDiscount?.toFixed(2)}</span>
                  </div>
                </div>
              )}

              <div className="pt-3 border-t border-gray-100 flex justify-between items-center">
                <span className="text-gray-600 font-medium">{t("text_tobepaid_amount")}</span>
                <span className="text-[#e80d44] text-2xl font-bold tracking-tight">¥{Math.max(0, (plans?.super?.amount || 199.00) - couponDiscount).toFixed(2)}</span>
              </div>
            </div>

            <div>
              <Button 
                type="primary" 
                loading={applyLoading}
                className="w-full bg-[#e91e63] hover:bg-[#d81557] border-none h-[44px] rounded-lg text-base font-bold mt-4"
                onClick={handleConfirmPayment}
              >
                {t('text_confirm_payment')}
              </Button>
            </div>
          </div>
        )}
        <Modal
          open={showCloseModal}
          onCancel={() => {
            setShowCloseModal(false);
          }}
          closeable={true}
          footer={null}
          centered
          width={400}
          title={null}
          styles={{
            body: {
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '24px 16px 32px 16px',
            }
          }}
        >
          <div className="flex flex-col items-center justify-center p-2">
            <div style={{padding: 12}} className="bg-[#fff0f4] rounded-full">
              <BsInfo size={36} color="#e80d44" />
            </div>
            <span className="text-sm shrink-0 font-medium mt-4">{t("text_qrcode_warning")}</span>
            <button
              onClick={() => {
                setShowCloseModal(false);
                setTimeout(() => {
                  handleClose();
                }, 100);
              }}
              style={{width: '100%'}}
              className={`rounded-xl bg-gradient-to-r mt-4 from-[#e80d44] to-[#ff3b68] text-white px-10 py-3 font-semibold text-sm transition-all duration-300 flex justify-center items-center gap-2 hover:scale-105 active:scale-95`}
            >
              {t("text_qrcode_confirm")}
            </button>
          </div>
        </Modal>
      </Modal>

      <style jsx global>{`
        .super-vip-modal .ant-modal-content {
          border-radius: 16px;
          overflow: hidden;
        }
      `}</style>
    </Modal>
  );
};

export default SuperVIPModal;
